import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Check, Edit3, Eye, MessageSquare, Star, GripVertical, Trash2, Settings, HelpCircle, X, ArrowLeft, ChevronUp, ChevronDown, CheckSquare } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";

const questionSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(5, "Question must be at least 5 characters"),
  type: z.enum(['text', 'rating', 'multiple-choice'], {
    required_error: "Please select a question type",
  }),
  required: z.boolean().default(false),
  options: z.array(z.string()).optional(),
});

const templateSchema = z.object({
  name: z.string().min(2, "Template name must be at least 2 characters"),
  description: z.string().optional(),
  templateType: z.enum(['feedback', 'one_on_one'], {
    required_error: "Please select a template type",
  }),
  category: z.string().min(1, "Please select a category"),
  questions: z.array(questionSchema).min(1, "Template must have at least one question"),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface UnifiedTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (templateData: TemplateFormData) => Promise<void>;
  existingTemplate?: any;
  mode: 'create' | 'edit' | 'preview';
}

const CATEGORIES = {
  feedback: [
    { value: 'performance', label: 'Performance Review' },
    { value: 'assessment', label: '360 Assessment' },
    { value: 'project', label: 'Project Feedback' },
    { value: 'general', label: 'General Feedback' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'exit', label: 'Exit Interview' },
  ],
  one_on_one: [
    { value: 'weekly', label: 'Weekly Check-in' },
    { value: 'monthly', label: 'Monthly Review' },
    { value: 'quarterly', label: 'Quarterly Planning' },
    { value: 'goal_setting', label: 'Goal Setting' },
    { value: 'career_development', label: 'Career Development' },
    { value: 'general', label: 'General Discussion' },
  ],
};

export function UnifiedTemplateModal({
  isOpen,
  onClose,
  onSave,
  existingTemplate,
  mode
}: UnifiedTemplateModalProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isPreviewMode, setIsPreviewMode] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("basic-info");
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Reset tab state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab("basic-info");
    }
  }, [isOpen]);

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: existingTemplate?.name || "",
      description: existingTemplate?.description || "",
      templateType: existingTemplate?.templateType || "feedback",
      category: existingTemplate?.category || "",
      questions: existingTemplate?.questions?.map((q: any) => ({
        ...q,
        required: q.required || false,
        options: q.options || []
      })) || [
        { text: "", type: "text", required: false, options: [] }
      ],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const watchedTemplateType = form.watch("templateType");

  // Update form when existing template changes
  React.useEffect(() => {
    if (isOpen && existingTemplate) {
      form.reset({
        name: existingTemplate.name,
        description: existingTemplate.description,
        templateType: existingTemplate.templateType,
        category: existingTemplate.category,
        questions: existingTemplate.questions?.map((q: any) => ({
          ...q,
          required: q.required || false,
          options: q.options || []
        })) || [{ text: "", type: "text", required: false, options: [] }],
      });
    } else if (isOpen && !existingTemplate) {
      form.reset({
        name: "",
        description: "",
        templateType: "feedback",
        category: "",
        questions: [{ text: "", type: "text", required: false, options: [] }],
      });
    }
  }, [existingTemplate, isOpen, form]);

  const onSubmit = async (data: TemplateFormData) => {
    setIsSubmitting(true);
    try {
      await onSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const addQuestion = (type = "text") => {
    append({ text: "", type: type as "text" | "rating" | "multiple-choice", required: false, options: type === "multiple-choice" ? ["Option 1", "Option 2"] : [] });
  };

  const moveQuestion = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index > 0) {
      move(index, index - 1);
    } else if (direction === "down" && index < fields.length - 1) {
      move(index, index + 1);
    }
  };

  const handleDragStart = (event: React.DragEvent<HTMLButtonElement>, index: number) => {
    if (isReadOnly) return;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (isReadOnly || draggedIndex === null || draggedIndex === index) {
      return;
    }
    setDragOverIndex(index);
  };

  const handleDragLeave = (index: number) => {
    if (isReadOnly || dragOverIndex !== index) {
      return;
    }
    setDragOverIndex(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isReadOnly || draggedIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    if (isReadOnly || draggedIndex === null) return;
    event.preventDefault();
    const sourceIndex = draggedIndex;
    if (sourceIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    move(sourceIndex, index);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create Template';
      case 'edit':
        return 'Edit Template';
      case 'preview':
        return 'Template Preview';
      default:
        return 'Template';
    }
  };

  const getModalIcon = () => {
    switch (mode) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'edit':
        return <Edit3 className="h-4 w-4 text-blue-600" />;
      case 'preview':
        return <Eye className="h-4 w-4 text-gray-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
    }
  };

  const getIconBgColor = () => {
    switch (mode) {
      case 'create':
        return 'bg-blue-100';
      case 'edit':
        return 'bg-blue-100';
      case 'preview':
        return 'bg-gray-100';
      default:
        return 'bg-gray-100';
    }
  };

  const isReadOnly = mode === 'preview' || isPreviewMode;
  const currentCategories = CATEGORIES[watchedTemplateType] || CATEGORIES.feedback;

  const togglePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[95vh] min-h-0 overflow-hidden flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b border-border bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${getIconBgColor()} rounded-lg flex items-center justify-center`}>
                {getModalIcon()}
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">{getModalTitle()}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {mode === 'create' ? 'Design a custom template for your organization' :
                   mode === 'edit' ? 'Update your template details and questions' :
                   'Review your template configuration'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {mode !== 'preview' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 h-8"
                  onClick={togglePreview}
                  type="button"
                >
                  {isPreviewMode ? (
                    <>
                      <ArrowLeft className="h-3 w-3" />
                      Back to Edit
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      Preview
                    </>
                  )}
                </Button>
              )}
              <button
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors ml-2"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </DialogHeader>

        {isPreviewMode ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Preview Mode */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Template Header */}
              <div className="border-b border-border pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center border border-blue-200">
                    <MessageSquare className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-foreground mb-2">{form.watch('name') || 'Template Name'}</h2>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-medium">
                        {form.watch('templateType') === 'one_on_one' ? '1-on-1 Template' : 'Feedback Template'}
                      </Badge>
                      {form.watch('category') && (
                        <Badge variant="outline" className="font-medium">
                          {currentCategories.find(c => c.value === form.watch('category'))?.label}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {form.watch('description') && (
                  <p className="text-muted-foreground text-base leading-relaxed">{form.watch('description')}</p>
                )}
              </div>

              {/* Questions Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Questions ({fields.length})
                </h3>
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <Card key={field.id} className="p-4 bg-muted/30 border-l-4 border-l-primary">
                      <div className="flex items-start gap-4">
                        <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-sm font-semibold text-primary-foreground">
                          {index + 1}
                        </div>
                        <div className="flex-1 space-y-3">
                          <div>
                            <p className="font-medium text-foreground text-base leading-relaxed">
                              {form.watch(`questions.${index}.text`) || 'Question text...'}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              {form.watch(`questions.${index}.type`) === 'rating' ? (
                                <>
                                  <Star className="h-4 w-4 text-amber-500" />
                                  <span className="text-sm text-muted-foreground font-medium">Rating Scale (1-5)</span>
                                </>
                              ) : form.watch(`questions.${index}.type`) === 'multiple-choice' ? (
                                <>
                                  <CheckSquare className="h-4 w-4 text-green-500" />
                                  <span className="text-sm text-muted-foreground font-medium">Multiple Choice</span>
                                </>
                              ) : (
                                <>
                                  <MessageSquare className="h-4 w-4 text-blue-500" />
                                  <span className="text-sm text-muted-foreground font-medium">Text Response</span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Sample Answer Area */}
                          <div className="pt-2">
                            {form.watch(`questions.${index}.type`) === 'rating' ? (
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((rating) => (
                                  <Star key={rating} className="h-5 w-5 text-gray-300 hover:text-amber-400 cursor-pointer transition-colors" />
                                ))}
                                <span className="ml-2 text-sm text-muted-foreground">Select rating</span>
                              </div>
                            ) : form.watch(`questions.${index}.type`) === 'multiple-choice' ? (
                              <div className="space-y-2">
                                {(form.watch(`questions.${index}.options`) || ['Option 1', 'Option 2']).map((option: string, optIndex: number) => (
                                  <label key={optIndex} className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name={`preview-${index}`} className="text-blue-600" />
                                    <span className="text-sm text-foreground">{option}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="w-full p-3 border border-border rounded-lg bg-background text-sm text-muted-foreground">
                                Type your response here...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {fields.length === 0 && (
                  <Card className="p-8 text-center border-dashed">
                    <HelpCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground font-medium">No questions added yet</p>
                    <p className="text-sm text-muted-foreground/80 mt-1">Switch back to edit mode to add questions</p>
                  </Card>
                )}
              </div>
            </div>

            {/* Preview Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Eye className="h-4 w-4" />
                Preview Mode
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={togglePreview}
                  className="gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Template
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex flex-col flex-1 min-h-0">
                <div className="px-6 py-0 bg-white border-b border-border">
                  <TabsList className="h-auto bg-transparent p-0 border-0">
                    <TabsTrigger
                      value="basic-info"
                      onClick={() => {
                        handleTabChange("basic-info");
                      }}
                      className="px-4 py-3 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent rounded-none font-medium"
                    >
                      Basic Info
                    </TabsTrigger>
                    <TabsTrigger
                      value="questions"
                      onClick={() => {
                        handleTabChange("questions");
                      }}
                      className="px-4 py-3 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent rounded-none font-medium"
                    >
                      Questions
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0">
                  <TabsContent value="basic-info" className="p-6 space-y-6 mt-0">
                    {/* Basic Information */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Settings className="h-5 w-5 text-primary" />
                          Template Details
                        </h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium">Template Name *</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter template name..."
                                    disabled={isReadOnly}
                                    className="h-10"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="templateType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium">Template Type *</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  disabled={isReadOnly}
                                >
                                  <FormControl>
                                    <SelectTrigger className="h-10">
                                      <SelectValue placeholder="Select template type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="feedback">
                                      <div className="flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-blue-500" />
                                        Feedback Template
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="one_on_one">
                                      <div className="flex items-center gap-2">
                                        <Star className="h-4 w-4 text-amber-500" />
                                        1-on-1 Template
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <FormField
                          control={form.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">Category *</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={isReadOnly}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select a category" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {currentCategories.map((category) => (
                                    <SelectItem key={category.value} value={category.value}>
                                      {category.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">Description</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Enter template description..."
                                  rows={4}
                                  disabled={isReadOnly}
                                  className="resize-none"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="questions" className="p-0 space-y-0 mt-0">
                    {/* Question Type Selector Bar */}
                    {!isReadOnly && (
                      <div className="px-6 py-4 border-b border-border bg-gray-50">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700">Add question:</span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addQuestion("text")}
                              className="gap-2 h-8 text-xs font-medium hover:bg-blue-50 hover:border-blue-300"
                            >
                              <MessageSquare className="h-3 w-3" />
                              Text Response
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addQuestion("rating")}
                              className="gap-2 h-8 text-xs font-medium hover:bg-blue-50 hover:border-blue-300"
                            >
                              <Star className="h-3 w-3" />
                              Rating Scale
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addQuestion("multiple-choice")}
                              className="gap-2 h-8 text-xs font-medium hover:bg-blue-50 hover:border-blue-300"
                            >
                              <CheckSquare className="h-3 w-3" />
                              Multiple Choice
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Questions Section */}
                    <div className="p-6 space-y-4">
                      <div className="space-y-4">
                        {fields.map((field, index) => {
                          const isDragging = !isReadOnly && draggedIndex === index;
                          const isDropTarget =
                            !isReadOnly && dragOverIndex === index && draggedIndex !== null && draggedIndex !== index;

                          return (
                            <Card
                              key={field.id}
                              className={cn(
                                "border-2 border-gray-200 rounded-lg bg-white shadow-sm transition-colors",
                                isDragging && "opacity-80 ring-2 ring-blue-300",
                                isDropTarget && "border-blue-400 bg-blue-50/60"
                              )}
                              onDragEnter={() => handleDragEnter(index)}
                              onDragOver={handleDragOver}
                              onDragLeave={() => handleDragLeave(index)}
                              onDrop={(event) => handleDrop(event, index)}
                            >
                              <CardContent className="p-6">
                                <div className="flex items-start gap-4">
                                  {/* Drag Handle and Question Number */}
                                  <div className="flex flex-col items-center gap-2">
                                    {!isReadOnly && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        draggable={!isReadOnly}
                                        onDragStart={(event) => handleDragStart(event, index)}
                                        onDragEnd={handleDragEnd}
                                        aria-label="Reorder question"
                                        className="h-6 w-6 p-0 cursor-grab hover:bg-gray-100 text-gray-400"
                                      >
                                        <GripVertical className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600">
                                      {index + 1}
                                    </div>
                                  </div>

                                  <div className="flex-1 space-y-4">
                                  {/* Question Header */}
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                      <Badge variant="outline" className="text-xs font-medium bg-gray-50">
                                        {form.watch(`questions.${index}.type`) === 'text' && 'Text Response'}
                                        {form.watch(`questions.${index}.type`) === 'rating' && 'Rating Scale'}
                                        {form.watch(`questions.${index}.type`) === 'multiple-choice' && 'Multiple Choice'}
                                      </Badge>

                                      {/* Question Type Selector */}
                                      {!isReadOnly && (
                                        <FormField
                                          control={form.control}
                                          name={`questions.${index}.type`}
                                          render={({ field: typeField }) => (
                                            <FormItem className="flex-1">
                                              <Select
                                                onValueChange={typeField.onChange}
                                                value={typeField.value}
                                                disabled={isReadOnly}
                                              >
                                                <FormControl>
                                                  <SelectTrigger className="w-40 h-8 text-xs">
                                                    <SelectValue placeholder="Select type" />
                                                  </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                  <SelectItem value="text">
                                                    <div className="flex items-center gap-2">
                                                      <MessageSquare className="h-3 w-3 text-blue-500" />
                                                      <span className="text-xs">Text Response</span>
                                                    </div>
                                                  </SelectItem>
                                                  <SelectItem value="rating">
                                                    <div className="flex items-center gap-2">
                                                      <Star className="h-3 w-3 text-amber-500" />
                                                      <span className="text-xs">Rating Scale</span>
                                                    </div>
                                                  </SelectItem>
                                                  <SelectItem value="multiple-choice">
                                                    <div className="flex items-center gap-2">
                                                      <CheckSquare className="h-3 w-3 text-green-500" />
                                                      <span className="text-xs">Multiple Choice</span>
                                                    </div>
                                                  </SelectItem>
                                                </SelectContent>
                                              </Select>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}
                                    </div>

                                    {/* Controls */}
                                    <div className="flex items-center gap-1">
                                      {!isReadOnly && (
                                        <>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => moveQuestion(index, "up")}
                                            disabled={index === 0}
                                            className="h-8 w-8 p-0 hover:bg-gray-100"
                                          >
                                            <ChevronUp className="h-3 w-3 text-gray-500" />
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => moveQuestion(index, "down")}
                                            disabled={index === fields.length - 1}
                                            className="h-8 w-8 p-0 hover:bg-gray-100"
                                          >
                                            <ChevronDown className="h-3 w-3 text-gray-500" />
                                          </Button>
                                          {fields.length > 1 && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => remove(index)}
                                              className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Required Toggle */}
                                  {!isReadOnly && (
                                    <FormField
                                      control={form.control}
                                      name={`questions.${index}.required`}
                                      render={({ field }) => (
                                        <FormItem className="flex items-center gap-3 space-y-0">
                                          <FormControl>
                                            <Switch
                                              checked={field.value}
                                              onCheckedChange={field.onChange}
                                            />
                                          </FormControl>
                                          <FormLabel className="text-sm font-medium text-gray-700 cursor-pointer">
                                            Required question
                                          </FormLabel>
                                          {field.value && (
                                            <div className="flex items-center gap-1">
                                              <Check className="h-4 w-4 text-blue-600" />
                                            </div>
                                          )}
                                        </FormItem>
                                      )}
                                    />
                                  )}

                                  {/* Question Text */}
                                  <FormField
                                    control={form.control}
                                    name={`questions.${index}.text`}
                                    render={({ field: questionField }) => (
                                      <FormItem>
                                        <FormControl>
                                          <Textarea
                                            placeholder="Enter your question..."
                                            rows={3}
                                            disabled={isReadOnly}
                                            className="resize-none border-gray-200"
                                            {...questionField}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />

                                  {/* Multiple Choice Options */}
                                  {form.watch(`questions.${index}.type`) === 'multiple-choice' && !isReadOnly && (
                                    <div className="space-y-2">
                                      <FormLabel className="text-sm font-medium text-gray-700">Options</FormLabel>
                                      <div className="space-y-2">
                                        {(form.watch(`questions.${index}.options`) || []).map((option: string, optionIndex: number) => (
                                          <div key={optionIndex} className="flex items-center gap-2">
                                            <Input
                                              value={option}
                                              onChange={(e) => {
                                                const options = form.getValues(`questions.${index}.options`) || [];
                                                options[optionIndex] = e.target.value;
                                                form.setValue(`questions.${index}.options`, options);
                                              }}
                                              placeholder={`Option ${optionIndex + 1}`}
                                              className="h-8 text-sm"
                                            />
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                const options = form.getValues(`questions.${index}.options`) || [];
                                                options.splice(optionIndex, 1);
                                                form.setValue(`questions.${index}.options`, options);
                                              }}
                                              className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ))}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const options = form.getValues(`questions.${index}.options`) || [];
                                            form.setValue(`questions.${index}.options`, [...options, `Option ${options.length + 1}`]);
                                          }}
                                          className="h-8 text-xs"
                                        >
                                          <Plus className="h-3 w-3 mr-1" />
                                          Add Option
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>

                      {/* Add Another Question Button */}
                      {!isReadOnly && fields.length > 0 && (
                        <div className="flex justify-center pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => addQuestion("text")}
                            className="gap-2 text-sm font-medium"
                          >
                            <Plus className="h-4 w-4" />
                            Add Another Question
                          </Button>
                        </div>
                      )}

                      {fields.length === 0 && (
                        <Card className="p-8 text-center border-dashed border-2 border-gray-300">
                          <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                          <h4 className="text-lg font-medium mb-2 text-gray-900">No questions added yet</h4>
                          <p className="text-sm text-gray-600 mb-4">
                            Start building your template by adding your first question using the buttons above
                          </p>
                        </Card>
                      )}
                    </div>
                  </TabsContent>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-white gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="h-9 px-4 font-medium border-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  {!isReadOnly && (
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="gap-2 h-9 px-6 font-medium bg-blue-600 hover:bg-blue-700"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          {mode === 'create' ? 'Create Template' : 'Update Template'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Tabs>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
